<script setup>
  import { reactive, ref, watch } from 'vue'
  import i18n from "@/lang/i18n";
  import { VueDatePicker } from '@vuepic/vue-datepicker';
  import '@vuepic/vue-datepicker/dist/main.css'
  import { ja } from 'date-fns/locale'

  const props = defineProps({
    operationOfficeInfo: Object,
    operationOfficeLists: Array,
    windowInnerWidth: Number
  })
  const emits = defineEmits(['selected'])
  const title = i18n.global.t('pages.installationOffices.form.operationOffice')
  const officeInCharges = reactive({ value: [] })
  const manageOfficeOfCompanies = reactive({ value: [] })
  const selectedList = reactive({ value: [] })
  const candidateList = reactive({ value: [] })
  const jp = ja;

  watch(() => props.operationOfficeLists, (officeList) => {
    manageOfficeOfCompanies.value = officeList
  })

  watch(() => props.operationOfficeInfo, (info) => {
    const targetIds = pluckIdListString(info)
    const selectedIds = pluckIdListString(selectedList.value)
    if (selectedIds == targetIds) return

    officeInCharges.value = info
    selectedList.value = officeInCharges.value.map((office) => {
      return {
        id: office.id,
        operation_office_name: office.operation_office_name,
        expired_at: office.pivot?.expired_at
      }
    })
  })

  watch(() => manageOfficeOfCompanies.value, () => {
    makeCandidateAndSelectedLists(selectedList.value)
  })

  watch(() => selectedList.value, (selected) => {
    makeCandidateAndSelectedLists(selected)

    const emitValues = selected.filter((office) => { return !manageOfficeOfCompanies.value.includes(office.id) })
    emits('selected', { office_in_charge: emitValues })
  },
  { deep: true });

  function makeCandidateAndSelectedLists (selected) {
    const officeIds = selected.map((office) => { return office.id })
    if (manageOfficeOfCompanies.value == {}) return
    const candidateAccounts = manageOfficeOfCompanies.value.filter((office) => { return !officeIds.includes(office.id) })
    candidateList.value = candidateAccounts.map((office) => {
      return { id: office.id, operation_office_name: office.name }
    })
  }

  const selectInSelectedList = ref([])
  const selectInCandidateList = ref([])

  function selectItemInSelectedList(id) {
    if (selectInSelectedList.value.includes(id)) {
      selectInSelectedList.value = selectInSelectedList.value.filter(item => item !== id)
    } else {
      selectInSelectedList.value.push(id)
    }
  }

  const addOfficeInCharge = () => {
    const targetAccounts = candidateList.value.filter((officeInfo) => {
      return Object.values(selectInCandidateList.value).includes(officeInfo.id)
    })

    selectedList.value = selectedList.value.concat(targetAccounts)
  }

  const clearOfficeInCharge = () => {
    selectedList.value = selectedList.value.filter((officeInfo) => {
      return !Object.values(selectInSelectedList.value).includes(officeInfo.id)
    })
    selectInSelectedList.value = [];
  }

  const pluckIdListString = (arr) => {
    const ids = []
    arr.forEach((obj) => {
      ids.push(obj.id)
    })
    return JSON.stringify(ids.sort())
  }

  function selectedItemClass(id) {
    return selectInSelectedList.value.includes(id) ? 'bg-blue-lighten-4' : '';
  }
</script>

<template>
  <div v-show="windowInnerWidth > 800" class="mb-4">
    <v-row class="checkbox-with-number-text-form pl-4 pr-3 mb-2">
      <v-col cols="2">
        <h6 class="pt-2 pl-4 fw-bold">{{title}}</h6>
      </v-col>
      <v-col cols="10">
        <v-row>
          <v-col cols="5">
            <div>
              <div class="pt-2 fw-bold label">
                {{ i18n.global.t('pages.installationOffices.form.list') }}
              </div>
              <select class="form-control form-height" multiple="multiple" v-model="selectInCandidateList">
                <option v-for="(officeInfo, index) in candidateList.value" :key="index" :value="officeInfo.id">
                  {{officeInfo.operation_office_name}}
                </option>
              </select>
            </div>
          </v-col>
          <v-col class="d-flex justify-center align-center">
            <div class="text-center mt-40px">
              <div>
                <v-btn
                  class="mb-2 fw-bold btn-color"
                  @click="addOfficeInCharge"
                >
                  ▶︎
                </v-btn>
              </div>
              <div>
                <v-btn
                  class="fw-bold btn-color"
                  @click="clearOfficeInCharge"
                >
                  ◀︎
                </v-btn>
              </div>
            </div>
          </v-col>
          <v-col cols="5">
            <div>
              <div class="pt-2 fw-bold label">
                {{ i18n.global.t('pages.installationOffices.form.selected') }}
              </div>
              <div class="form-selected">
                <v-row class="header-row mx-3 my-1 py-1">
                  <v-col cols="7" class="table-header d-flex justify-center">
                    {{ i18n.global.t('pages.installationOffices.form.operationOfficeName') }}
                  </v-col>
                  <v-col cols="5" class="table-header d-flex justify-center">
                    {{ i18n.global.t('pages.installationOffices.form.expiryDate') }}
                  </v-col>
                </v-row>
                <v-row
                  v-for="(officeInfo, index) in selectedList.value" :key="index" :value="officeInfo.id"
                  class="mx-2 mt-0"
                  :class="selectedItemClass(officeInfo.id)"
                >
                  <v-col
                    cols="7"
                    class="d-flex align-center"
                    role="button"
                    @click="selectItemInSelectedList(officeInfo.id)"
                  >
                    {{officeInfo.operation_office_name}}
                  </v-col>
                  <v-col cols="5">
                    <VueDatePicker
                      :locale="ja"
                      teleport="body"
                      :z-index="12000"
                      :enable-time-picker="false"
                      v-model="officeInfo.expired_at"
                      :formats="{ input: 'yyyy/MM/dd' }"
                      :clearable="true"
                      auto-apply
                      :close-on-auto-apply="true"
                      :placeholder="officeInfo.expired_at"
                      :min-date="new Date()"
                    />
                  </v-col>
                </v-row>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </div>

  <div v-show="windowInnerWidth <= 800" class="mb-4">
    <v-row class="checkbox-with-number-text-form pl-4 pr-3">
      <v-col cols="3">
        <h6 class="pt-2 pl-4 fw-bold">{{title}}</h6>
      </v-col>
      <v-col>
        <div>
          <div class="pt-2 fw-bold label">
            {{ i18n.global.t('pages.installationOffices.form.list') }}
          </div>
          <select class="form-control form-height" multiple="multiple" v-model="selectInCandidateList">
            <option v-for="(officeInfo, index) in candidateList.value" :key="index" :value="officeInfo.id">
              {{officeInfo.operation_office_name}}
            </option>
          </select>
        </div>
      </v-col>
    </v-row>
    <v-row class="checkbox-with-number-text-form pl-4 pr-3">
      <v-col cols="3">
      </v-col>
      <v-col class="text-center">
        <div class="d-flex justify-content-center">
          <div class="mt-10px mr-10px">
            <v-btn
              class="fw-bold btn-color"
              @click="clearOfficeInCharge"
            >
              ◀︎
            </v-btn>
          </div>
          <div class="mt-10px">
            <v-btn
              class="mb-2 fw-bold btn-color"
              @click="addOfficeInCharge"
            >
              ▶︎
            </v-btn>
          </div>
        </div>
      </v-col>
    </v-row>
    <v-row class="checkbox-with-number-text-form pl-4 pr-3 mb-2">
      <v-col cols="3">
      </v-col>
      <v-col>
        <div>
          <div class="pt-2 fw-bold label">
            {{ i18n.global.t('pages.installationOffices.form.selected') }}
          </div>
          <div class="form-selected">
            <v-row class="header-row mx-3 my-1 py-1">
              <v-col cols="7" class="table-header d-flex justify-center">
                {{ i18n.global.t('pages.installationOffices.form.operationOfficeName') }}
              </v-col>
              <v-col cols="5" class="table-header d-flex justify-center">
                {{ i18n.global.t('pages.installationOffices.form.expiryDate') }}
              </v-col>
            </v-row>
            <v-row
              v-for="(officeInfo, index) in selectedList.value" :key="index" :value="officeInfo.id"
              class="mx-2 mt-0"
              :class="selectedItemClass(officeInfo.id)"
            >
              <v-col
                cols="7"
                class="d-flex align-center"
                role="button"
                @click="selectItemInSelectedList(officeInfo.id)"
              >
                {{officeInfo.operation_office_name}}
              </v-col>
              <v-col cols="5">
                <VueDatePicker
                  :locale="ja"
                  :enable-time-picker="false"
                  v-model="officeInfo.expired_at"
                  :format="`yyyy/MM/dd`"
                  :clearable="true"
                  auto-apply
                  :close-on-auto-apply="true"
                  :placeholder="officeInfo.expired_at"
                  :min-date="new Date()"
                />
              </v-col>
            </v-row>
          </div>
        </div>
      </v-col>
    </v-row>
  </div>
</template>
<style scoped>
.btn-color {
  color: white;
  background-color: #3388DC;
}
.label {
  font-size: 14px;
}
.form-height {
  height: 300px;
}
.form-selected {
  height: 300px;
  border: 1px solid #E3E3E3;
  border-radius: 4px;
  overflow-y: auto;
}
.header-row {
  border-bottom: 1px solid #E3E3E3;
}
.table-header {
  font-weight: bold;
  font-size: 0.85rem;
}
.mt-10px {
  margin-top: 10px;
}
.mr-10px {
  margin-right: 10px;
}
.mt-40px {
  margin-top: 40px;
}
</style>
